import type { EventoDataType } from "@/types/events"

const GOOGLE_CLIENT_ID = import.meta.env.VITE_GOOGLE_CLIENT_ID || ""
const MICROSOFT_CLIENT_ID = import.meta.env.VITE_MICROSOFT_CLIENT_ID || ""
const OAUTH_STATE_KEY = "giants_oauth_state"
const OAUTH_RESULT_KEY = "giants_oauth_result"

function stateKey(provider: "google" | "microsoft") {
  return `${OAUTH_STATE_KEY}_${provider}`
}

function resultKey(provider: "google" | "microsoft") {
  return `${OAUTH_RESULT_KEY}_${provider}`
}

type OAuthResult = { token?: string; error?: string }

export const GOOGLE_TOKEN_KEY = "giants_google_calendar_token"
export const MICROSOFT_TOKEN_KEY = "giants_microsoft_calendar_token"

// --- Token management ---
export const getGoogleToken = (): string | null => localStorage.getItem(GOOGLE_TOKEN_KEY)
export const getMicrosoftToken = (): string | null => localStorage.getItem(MICROSOFT_TOKEN_KEY)
export const setGoogleToken = (token: string) => localStorage.setItem(GOOGLE_TOKEN_KEY, token)
export const setMicrosoftToken = (token: string) =>
  localStorage.setItem(MICROSOFT_TOKEN_KEY, token)
export const disconnectGoogle = () => localStorage.removeItem(GOOGLE_TOKEN_KEY)
export const disconnectMicrosoft = () => localStorage.removeItem(MICROSOFT_TOKEN_KEY)
export const isGoogleConnected = (): boolean => !!getGoogleToken()
export const isMicrosoftConnected = (): boolean => !!getMicrosoftToken()

function generateState(provider: "google" | "microsoft"): string {
  const state = `${provider}:${crypto.randomUUID()}`
  localStorage.setItem(stateKey(provider), state)
  return state
}

export function parseProviderFromState(rawState: string | null): "google" | "microsoft" | null {
  const provider = rawState?.split(":")[0]
  return provider === "google" || provider === "microsoft" ? provider : null
}

export function oauthMessageTypes(provider: "google" | "microsoft") {
  return provider === "google"
    ? { success: "GOOGLE_OAUTH_SUCCESS", error: "GOOGLE_OAUTH_ERROR" }
    : { success: "MICROSOFT_OAUTH_SUCCESS", error: "MICROSOFT_OAUTH_ERROR" }
}

export function validateAndConsumeState(rawState: string | null): "google" | "microsoft" | null {
  const provider = parseProviderFromState(rawState)
  if (!provider) return null
  const key = stateKey(provider)
  const stored = localStorage.getItem(key)
  localStorage.removeItem(key)
  if (!stored || rawState !== stored) return null
  return provider
}

export function publishOAuthResult(provider: "google" | "microsoft", result: OAuthResult) {
  try {
    localStorage.setItem(resultKey(provider), JSON.stringify(result))
  } catch {
  }
}

// --- OAuth URL builders ---
function getGoogleOAuthUrl(): string {
  const redirectUri = `${window.location.origin}/oauth/callback`
  const params = new URLSearchParams({
    client_id: GOOGLE_CLIENT_ID,
    redirect_uri: redirectUri,
    response_type: "token",
    scope: "https://www.googleapis.com/auth/calendar.events.owned",
    state: generateState("google"),
    prompt: "consent",
  })
  return `https://accounts.google.com/o/oauth2/v2/auth?${params}`
}

function getMicrosoftOAuthUrl(): string {
  const redirectUri = `${window.location.origin}/oauth/callback`
  const params = new URLSearchParams({
    client_id: MICROSOFT_CLIENT_ID,
    redirect_uri: redirectUri,
    response_type: "token",
    scope: "openid profile Calendars.ReadWrite",
    state: generateState("microsoft"),
    response_mode: "fragment",
  })
  return `https://login.microsoftonline.com/common/oauth2/v2.0/authorize?${params}`
}

// --- Open OAuth and wait for token ---
function openOAuthTab(url: string, provider: "google" | "microsoft"): Promise<string> {
  return new Promise((resolve, reject) => {
    const { success: successType, error: errorType } = oauthMessageTypes(provider)
    const rKey = resultKey(provider)

    let settled = false
    let closedTimer: ReturnType<typeof setInterval> | undefined

    function cleanup() {
      if (closedTimer !== undefined) clearInterval(closedTimer)
      window.removeEventListener("message", messageHandler)
      window.removeEventListener("oauth_callback", nativeCallbackHandler)
      window.removeEventListener("storage", storageHandler)
    }

    function settle(result: OAuthResult | null | undefined) {
      if (settled || !result) return
      settled = true
      cleanup()
      localStorage.removeItem(rKey)
      if (result.token) resolve(result.token)
      else reject(new Error(result.error || "Erro de autenticação"))
    }

    function messageHandler(event: MessageEvent) {
      if (event.origin !== window.location.origin) return
      if (event.data?.type === successType) {
        settle({ token: event.data.token })
      } else if (event.data?.type === errorType || event.data?.type === "OAUTH_ERROR") {
        settle({ error: event.data.error })
      }
    }

    function nativeCallbackHandler(event: Event) {
      const detail = (event as CustomEvent<{ type?: string; token?: string; error?: string }>).detail
      if (!detail) return
      if (detail.type === successType && detail.token) {
        settle({ token: detail.token })
      } else if (detail.type === errorType) {
        settle({ error: detail.error })
      }
    }
    
    function storageHandler(event: StorageEvent) {
      if (event.key !== rKey || !event.newValue) return
      try {
        settle(JSON.parse(event.newValue) as OAuthResult)
      } catch {
      }
    }

    window.addEventListener("message", messageHandler)
    window.addEventListener("oauth_callback", nativeCallbackHandler)
    window.addEventListener("storage", storageHandler)

    const tab = window.open(url, "_blank")

    if (tab && !tab.closed) {
      closedTimer = setInterval(() => {
        if (!tab.closed) return
        const pending = localStorage.getItem(rKey)
        if (pending) {
          try {
            settle(JSON.parse(pending) as OAuthResult)
          } catch {
            // ignore malformed payload
          }
        }
        if (!settled) {
          settled = true
          cleanup()
          reject(new Error("Autenticação cancelada pelo usuário"))
        }
      }, 500)
    } else if (!tab) {
      cleanup()
      reject(new Error("Popup bloqueado pelo navegador. Permita popups para este site e tente novamente."))
    }
  })
}

// --- Connect calendars ---
export async function connectGoogleCalendar(): Promise<void> {
  if (!GOOGLE_CLIENT_ID) {
    throw new Error(
      "Google Client ID não configurado. Adicione VITE_GOOGLE_CLIENT_ID ao arquivo .env"
    )
  }
  const token = await openOAuthTab(getGoogleOAuthUrl(), "google")
  setGoogleToken(token)
}

export async function connectMicrosoftCalendar(): Promise<void> {
  if (!MICROSOFT_CLIENT_ID) {
    throw new Error(
      "Microsoft Client ID não configurado. Adicione VITE_MICROSOFT_CLIENT_ID ao arquivo .env"
    )
  }
  const token = await openOAuthTab(getMicrosoftOAuthUrl(), "microsoft")
  setMicrosoftToken(token)
}

// --- Add event to Google Calendar ---
export async function addEventToGoogleCalendar(evento: EventoDataType): Promise<void> {
  const token = getGoogleToken()
  if (!token) throw new Error("Google Calendar não conectado")

  const body = {
    summary: evento.descricao,
    location: evento.local || evento.endereco || "",
    start: {
      dateTime: new Date(evento.data_inicio).toISOString(),
      timeZone: "America/Sao_Paulo",
    },
    end: {
      dateTime: new Date(evento.data_fim).toISOString(),
      timeZone: "America/Sao_Paulo",
    },
  }

  const response = await fetch(
    "https://www.googleapis.com/calendar/v3/calendars/primary/events",
    {
      method: "POST",
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify(body),
    }
  )

  if (!response.ok) {
    if (response.status === 401) {
      disconnectGoogle()
      throw new Error("Sessão Google expirada. Reconecte sua conta no perfil.")
    }
    const err = await response.json().catch(() => ({}))
    throw new Error(
      (err as any)?.error?.message || "Erro ao adicionar evento ao Google Calendar"
    )
  }
}

// --- Add event to Microsoft Calendar ---
export async function addEventToMicrosoftCalendar(evento: EventoDataType): Promise<void> {
  const token = getMicrosoftToken()
  if (!token) throw new Error("Microsoft Calendar não conectado")

  const body = {
    subject: evento.descricao,
    location: { displayName: evento.local || evento.endereco || "" },
    start: {
      dateTime: new Date(evento.data_inicio).toISOString().replace("Z", ""),
      timeZone: "E. South America Standard Time",
    },
    end: {
      dateTime: new Date(evento.data_fim).toISOString().replace("Z", ""),
      timeZone: "E. South America Standard Time",
    },
  }

  const response = await fetch("https://graph.microsoft.com/v1.0/me/events", {
    method: "POST",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify(body),
  })

  if (!response.ok) {
    if (response.status === 401) {
      disconnectMicrosoft()
      throw new Error("Sessão Microsoft expirada. Reconecte sua conta no perfil.")
    }
    const err = await response.json().catch(() => ({}))
    throw new Error(
      (err as any)?.error?.message || "Erro ao adicionar evento ao Microsoft Calendar"
    )
  }
}
